
import React from 'react';
import FilterControls from '../FilterControls';
import PledgeCounter from '../PledgeCounter';
import { Region, ChurchPresence } from '../../data/types/region-types';
import { Button } from '@/components/ui/button';
import { Filter, MapPin, X } from 'lucide-react';
import { useLanguage } from '../../contexts/language';
import { useIsMobile } from '../../hooks/use-mobile';

interface FilterPanelProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  churchPresenceFilter: ChurchPresence | 'All';
  setChurchPresenceFilter: (filter: ChurchPresence | 'All') => void;
  fundingFilter: [number, number];
  setFundingFilter: (filter: [number, number]) => void;
  resetFilters: () => void;
  regions: Region[];
  onRegionSelect: (region: Region) => void;
  onCitySelect?: (cityId: string) => void;
  totalPledges: number;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  isChiapasActive?: boolean;
  isHidalgoActive?: boolean;
  showFunding?: boolean; 
  setShowFunding?: (show: boolean) => void;
  showLowPresence?: boolean;
  setShowLowPresence?: (show: boolean) => void;
  showUnreached?: boolean;
  setShowUnreached?: (show: boolean) => void;
  showAreasOfNeed?: boolean;
  setShowAreasOfNeed?: (show: boolean) => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  searchTerm,
  setSearchTerm,
  churchPresenceFilter,
  setChurchPresenceFilter,
  fundingFilter,
  setFundingFilter,
  resetFilters,
  regions,
  onRegionSelect,
  onCitySelect,
  totalPledges,
  showFilters,
  setShowFilters,
  isChiapasActive,
  isHidalgoActive,
  showFunding = true,
  setShowFunding = () => {},
  showLowPresence = true,
  setShowLowPresence = () => {},
  showUnreached = true,
  setShowUnreached = () => {},
  showAreasOfNeed = true,
  setShowAreasOfNeed = () => {}
}) => {
  const { t } = useLanguage();
  const { isMobile } = useIsMobile();
  
  // Determine active special region for styling
  const activeSpecialRegion = isChiapasActive ? 'chiapas' : isHidalgoActive ? 'hidalgo' : null;
  
  if (!showFilters && !isMobile) return null;
  
  return (
    <>
      {/* Filters panel - only show when showFilters is true */}
      <div 
        className={`
          transition-all duration-300 ease-in-out z-20
          ${showFilters ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          absolute top-0 right-0 md:w-80 h-auto max-h-[calc(100vh-4rem)]
          md:top-4 md:right-4 md:max-w-sm md:rounded-xl overflow-auto
        `}
      >
        <div className={`
          bg-white/95 backdrop-blur-sm shadow-lg p-3 md:p-4 rounded-xl border 
          ${activeSpecialRegion === 'chiapas' ? 'border-purple-500/50' : 
            activeSpecialRegion === 'hidalgo' ? 'border-orange-500/50' : 'border-jireh-border'} 
          ${activeSpecialRegion === 'chiapas' ? 'shadow-purple-300/30' : 
            activeSpecialRegion === 'hidalgo' ? 'shadow-orange-300/30' : ''}
        `}>
          <div className="flex justify-between items-center mb-2">
            <div className="w-full">
              <PledgeCounter totalPledges={totalPledges} />
            </div>
            
            {/* Information badge about focused search */}
            {(!isChiapasActive && !isHidalgoActive) && (
              <div className="inline-flex items-center p-1 px-2 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                <MapPin className="h-3 w-3 mr-1" />
                {t('specialRegionsOnly')}
              </div>
            )}
            
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 absolute top-2 right-2" 
              onClick={() => setShowFilters(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <FilterControls
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            churchPresenceFilter={churchPresenceFilter}
            setChurchPresenceFilter={setChurchPresenceFilter}
            fundingFilter={fundingFilter}
            setFundingFilter={setFundingFilter}
            resetFilters={resetFilters}
            regions={regions}
            onRegionSelect={onRegionSelect}
            onCitySelect={onCitySelect}
            isChiapasActive={isChiapasActive}
            isHidalgoActive={isHidalgoActive}
            showFunding={showFunding}
            setShowFunding={setShowFunding}
            showLowPresence={showLowPresence}
            setShowLowPresence={setShowLowPresence}
            showUnreached={showUnreached}
            setShowUnreached={setShowUnreached}
            showAreasOfNeed={showAreasOfNeed}
            setShowAreasOfNeed={setShowAreasOfNeed}
          />
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
